<template>
  <v-dialog
    v-model="show"
    class="modal"
    :transition="props.transition"
    :fullscreen="isFullscreen"
    :persistent="props.persistent"
    :no-click-animation="true"
    :scrim="props.scrim"
    :class="[
      modalBackgroundColor,
      !props.fullHeight && isFullscreen ? 'modal-auto-height' : '',
      `modal-type-${props.type}`,
      props.classModal,
    ]"
  >
    <div v-if="props.showHeader" class="modal__header">
      <h5 class="modal__title">{{ title }}</h5>

      <button
        v-if="props.closeable"
        class="modal__close-button"
        variant="text"
        rounded
        @click="handleToggle"
      >
        <img src="/images/close-neutral.svg" width="24" height="24" />
      </button>
    </div>

    <div
      v-if="show"
      class="modal__content"
      :class="[
        props.classContainer,
        props.padding ? 'modal-padding' : '',
        props.fullHeight ? 'modal-full-height' : '',
        !props.showHeader ? 'modal__content-no-header' : '',
        `modal-type-${props.type}`,
      ]"
    >
      <slot />
    </div>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';

type Transitions =
  | 'dialog-bottom-transition'
  | 'slide-y-transition'
  | 'slide-x-transition'
  | 'scale-transition'
  | 'fade-transition';

type ModalProps = {
  classContainer?: string;
  transition?: Transitions;
  title?: string | null;
  persistent?: boolean;
  modelValue: boolean;
  type?: 'fullscreen' | 'card' | 'align-top' | 'align-bottom' | 'full-top';
  showHeader?: boolean;
  backgroundColor?: string;
  padding?: boolean;
  fullHeight?: boolean;
  classModal?: string;
  scrim?: boolean;
  closeable?: boolean;
};

const props = withDefaults(defineProps<ModalProps>(), {
  title: null,
  classContainer: '',
  transition: 'dialog-bottom-transition',
  type: 'fullscreen',
  backgroundColor: 'contrast',
  showHeader: true,
  persistent: false,
  padding: true,
  fullHeight: true,
  classModal: '',
  scrim: true,
  closeable: true,
});

const isFullscreen = computed<boolean>(
  () =>
    props.type === 'fullscreen' ||
    props.type === 'align-top' ||
    props.type === 'align-bottom' ||
    props.type === 'full-top',
);

const show = ref(props.modelValue);

const modalBackgroundColor = computed(() => {
  if (props.backgroundColor === 'brand') {
    return `background-color-brand`;
  }

  return 'background-color-contrast-oninteractive';
});

const $emit = defineEmits(['update:modelValue']);

function handleToggle() {
  show.value = !show.value;
}

watch(
  () => show.value,
  () => {
    $emit('update:modelValue', show.value);
  },
);

watch(
  () => props.modelValue,
  () => {
    show.value = props.modelValue;
  },
);
</script>

<style lang="scss" scoped>
.modal {
  animation: fade-in 0.25s ease-in-out;

  :deep(.v-overlay__content) {
    border-radius: 24px;
    overflow: auto;
  }

  &.modal-full-height {
    :deep(.v-overlay__content) {
      height: 100%;
    }
  }

  &.modal-type-fullscreen {
    &.v-dialog--fullscreen {
      height: 85%;
      bottom: 0;
      top: initial;

      :deep(.v-overlay__content) {
        border-radius: 24px 24px 0 0;
        overflow: hidden;
      }
    }
  }

  &.modal-type-align-top {
    &.v-dialog--fullscreen {
      width: 90%;
      height: 85%;
      bottom: 10%;
    }
  }

  &.modal-type-align-bottom {
    &.v-dialog--fullscreen {
      width: 90%;
      height: 90%;
      top: 8%;
    }
  }

  &.modal-type-full-top {
    &.v-dialog--fullscreen {
      height: 90%;
      bottom: 0;
      top: initial;

      :deep(.v-overlay__content) {
        border-radius: 24px 24px 0 0;
        overflow: hidden;
      }
    }
  }

  &.modal-auto-height {
    height: 100%;

    :deep(.v-overlay__content) {
      height: initial;
      bottom: 0px;
      top: initial;
    }
  }

  &__header {
    display: flex;
    justify-content: center;
    align-items: center;

    position: relative;

    height: 32px;
    flex: none;
    margin-top: 24px;
    padding-left: 24px;
    padding-right: 24px;
  }

  &__close-button {
    position: absolute;
    right: 24px;

    padding: 0;

    width: 32px;
    height: 32px;

    color: var(--color-neutral-03);
    border-radius: 100%;
    border: 1px solid var(--color-neutral-03);

    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__title {
    font-size: 14px;
    line-height: 16.8px;
    color: var(--color-neutral-06);
    margin: 0;
  }

  &__content {
    height: 100%;
    overflow: auto;

    animation: fade-up 0.35s ease-in-out 0.15s both;

    &.modal-padding {
      padding-left: 20px;
      padding-right: 20px;
    }

    &.modal-type-fullscreen {
      padding-bottom: env(safe-area-inset-bottom);
    }
  }

  &__content-no-header {
    margin-top: 0px;
  }
}

.background-color-contrast-oninteractive {
  :deep(.v-overlay__content) {
    background-color: var(--color-contrast-oninteractive);
  }
}

.background-color-brand {
  :deep(.v-overlay__content) {
    background-color: var(--color-brand-01);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(35px);
  }

  to {
    opacity: 1;
    transform: translateY(0px);
  }
}
</style>
